import React, { useId, useState } from 'react';
import { makeStyles, tokens, shorthands, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Spinner } from '@fluentui/react-components';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/fluent-adapter';
import { Button, Input, Label, Badge, RadioGroup } from '@/fluent-adapter';
import { Alert, AlertDescription } from '@/fluent-adapter';
import { Separator } from '@/fluent-adapter';
import { RequiredIndicator } from '@/components/Accessibility/RequiredIndicator';
import { CheckmarkCircle24Regular as CheckCircle, Warning24Regular as AlertTriangle, DocumentText24Regular as FileText, Location24Regular as MapPin, Shield24Regular as Shield, Apps24Regular as Apps, Info24Regular as Info } from '@fluentui/react-icons';
import type {
  DomesticReviewForm,
  DomesticRequestFormData,
  Country,
  TypeOfRequestForm,
} from '../../store/types';
import { identifierTypeOptionMap } from '../../constants/identifierTypes';
import { getDisplayPhone } from '@/utils/phoneUtils';
import { FIELD_LIMITS } from '../../constants/fieldLimits';
import { LEGAL_STRINGS } from '@/constants/legalStrings';

interface ReviewSubmitStepProps {
  formData: DomesticReviewForm;
  allFormData: DomesticRequestFormData;
  country: Country;
  onFormChange: (data: Partial<DomesticReviewForm>) => void;
  onTypeOfRequestChange?: (data: Partial<TypeOfRequestForm>) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  errors?: Record<string, string>;
  isUSA?: boolean;
}

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXXL },
  header: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXS },
  title: { fontSize: '20px', fontWeight: 600 },
  muted: { color: tokens.colorNeutralForeground3 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr', columnGap: tokens.spacingHorizontalXXL, rowGap: tokens.spacingVerticalXXL, '@media (min-width: 768px)': { gridTemplateColumns: '1fr 1fr', columnGap: tokens.spacingHorizontalL }, '@media (min-width: 1024px)': { columnGap: tokens.spacingHorizontalXXL } },
  sectionTitle: { fontWeight: 600, display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS },
  smallText: { fontSize: '12px', color: tokens.colorNeutralForeground3 },
  fileRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...shorthands.padding(tokens.spacingVerticalS), borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground2 },
  badgeRow: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
  pillGroup: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
  identifierList: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS, marginTop: tokens.spacingVerticalS },
  identifierItem: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXXS, padding: tokens.spacingHorizontalS, borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground2, border: `1px solid ${tokens.colorNeutralStroke2}` },
  identifierLabel: { fontSize: '12px', color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.04em' },
  identifierValue: { fontWeight: tokens.fontWeightSemibold, wordBreak: 'break-word' },
  centerRow: { display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: tokens.spacingVerticalXL },
  submitButton: { minWidth: '200px' },
  submitButtonContent: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS },
  errorText: { color: tokens.colorStatusDangerForeground1 },
  infoList: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS, fontSize: '14px' },
  infoRow: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalXS, flexWrap: 'wrap' },
  infoLabel: { color: tokens.colorNeutralForeground3, minWidth: '160px' },
  infoValue: { fontWeight: 600 },
  ackGroup: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS, marginTop: tokens.spacingVerticalL },
  legalGroup: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS, marginTop: tokens.spacingVerticalL },
  checkboxRow: { display: 'flex', alignItems: 'flex-start', columnGap: tokens.spacingHorizontalS, marginTop: tokens.spacingVerticalM },
  alertSpacer: { marginBottom: tokens.spacingVerticalM },
  disabledButtonAlert: { marginBottom: tokens.spacingVerticalM, maxWidth: '500px', textAlign: 'center' },
  required: {
    color: tokens.colorStatusDangerForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
});

export const ReviewSubmitStep: React.FC<ReviewSubmitStepProps> = ({
  formData,
  allFormData,
  country,
  onFormChange,
  onTypeOfRequestChange,
  onSubmit,
  isSubmitting,
  errors = {},
  isUSA = true,
}) => {
  const [showAllDetails, setShowAllDetails] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const styles = useStyles();
  const waShieldTitleId = useId();
  const waShieldDescId = useId();

  const acknowledgement = formData.acknowledgement ?? '';
  const perjuryAcknowledgement = formData.perjuryAcknowledgement ?? '';

  const DATA_CATEGORY_LABELS: Record<string, string> = {
    BasicSubscriberInformation: 'Basic Subscriber Information',
    AuthenticationLogs: 'Authentication Logs',
    TransactionalData: 'Transactional Data',
    ServiceTelemetry: 'Service Telemetry',
    Content: 'Content',
    Other: 'Other',
  };

  const isPreservation = allFormData.step1.isPreservationRequest === true;
  const isChildExploitation = allFormData.step1.cpConcern === true;

  const getDisabledReason = (): string | null => {
    if (isSubmitting) {
      return null;
    }

    if (Object.keys(errors).length > 0) {
      return 'Please correct all validation errors listed above';
    }

    if (!isPreservation && !isChildExploitation && acknowledgement.trim().toLowerCase() !== 'i acknowledge') {
      return 'Please type "I acknowledge" in the acknowledgement field';
    }

    if (perjuryAcknowledgement.trim().toLowerCase() !== 'i acknowledge') {
      return 'Please type "I acknowledge" in the perjury declaration field';
    }

    if (allFormData.step1.isWAShieldLaw === null) {
      return 'Please answer the Washington State Shield Law question';
    }

    if (allFormData.step1.isWAShieldLaw === true &&
        allFormData.step1.waShieldLawAcknowledgement.trim().toLowerCase() !== 'i acknowledge') {
      return 'Please type "I acknowledge" for the Washington Shield Law';
    }

    return null;
  };

  // Helper function to get crime types
  const getSelectedCrimeTypes = () => {
    return Object.entries(allFormData.step2)
      .filter(([key, value]) => key !== 'isOtherDescription' && value === true)
      .map(([key]) => key.replace(/^is/, '').replace(/([A-Z])/g, ' $1').trim());
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDateRange = (startDate: string | null, endDate: string | null): string => {
    if (!startDate || !endDate) return 'Not specified';
    const parseLocalDate = (dateStr: string): Date => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const isUserNotificationPermitted = !allFormData.step3.hasNdo;

  const selectedCrimes = getSelectedCrimeTypes();
  const { selectedServiceIds, manualServices, identifiers } = allFormData.step5;

  // The payload's Services array is the preset checkbox selections plus the free-text
  // additions (see cmsMapping.ts), so the review must show both — otherwise an agent
  // signs off on a request whose preset services were never displayed to them.
  const submittedServices = [...selectedServiceIds, ...manualServices].filter(
    (service) => service.trim() !== '',
  );
  const uploadedFiles = allFormData.step6.files.filter((f) => f.uploaded);
  const agentName = [allFormData.step4.agentFirstName, allFormData.step4.agentLastName]
    .map(value => value?.trim())
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.root}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Review and Submit</h2>
        <p className={styles.muted}>
          Please review your request details and provide the required acknowledgments
        </p>
      </div>

      {/* Request Summary */}
      <Card>
        <CardHeader>
          <CardTitle className={styles.sectionTitle}>
            <CheckCircle />
            Request Summary
          </CardTitle>
          <CardDescription>
            Summary of your law enforcement request
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Step 1: Type of Request */}
          <div className={styles.infoList}>
            <h4 className={styles.sectionTitle}>
              <MapPin />
              Request Information
            </h4>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Country / Region</span>
              <span className={styles.infoValue}>{country.name}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>LE Reference Number</span>
              <span className={styles.infoValue}>{allFormData.step1.leReferenceNumber || '—'}</span>
            </div>
            {allFormData.step1.requestType && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Request Type</span>
                <span className={styles.infoValue}>{allFormData.step1.requestType}</span>
              </div>
            )}
            {allFormData.step1.requestSubType && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Request Sub-Type</span>
                <span className={styles.infoValue}>{allFormData.step1.requestSubType}</span>
              </div>
            )}
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Child Exploitation</span>
              <Badge variant={allFormData.step1.cpConcern ? 'destructive' : 'secondary'}>
                {allFormData.step1.cpConcern ? 'Yes' : 'No'}
              </Badge>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Preservation Request</span>
              <Badge variant={allFormData.step1.isPreservationRequest ? 'default' : 'secondary'}>
                {allFormData.step1.isPreservationRequest ? 'Yes' : 'No'}
              </Badge>
            </div>
            {!isPreservation && !isChildExploitation && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>User Notification Permitted</span>
                <Badge variant={isUserNotificationPermitted ? 'default' : 'secondary'}>
                  {isUserNotificationPermitted ? 'Yes' : 'No'}
                </Badge>
              </div>
            )}
            {allFormData.step1.hasDifferentRecipient && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Delivery Email</span>
                <span className={styles.infoValue}>{allFormData.step1.deliveryEmail}</span>
              </div>
            )}
          </div>

          <Separator />

          {/* Step 2: Nature of Crimes */}
          <div className={styles.infoList}>
            <h4 className={styles.sectionTitle}>Nature of Crimes</h4>
            <div className={styles.badgeRow}>
              {selectedCrimes.map((crime) => (
                <Badge key={crime} variant="outline">
                  {crime}
                </Badge>
              ))}
            </div>
            {allFormData.step2.isOtherDescription && (
              <div>
                <span className={styles.smallText}>Other Description:</span>
                <p style={{ fontSize: 14, marginTop: 4, padding: '8px', borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground2 }}>
                  {allFormData.step2.isOtherDescription}
                </p>
              </div>
            )}
          </div>

          {!isPreservation && !isChildExploitation && (
            <>
              <Separator />

              <div className={styles.infoList}>
                <h4 className={styles.sectionTitle}>
                  <Shield />
                  Non-Disclosure Order
                </h4>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Submitting NDO</span>
                  <Badge variant={allFormData.step3.hasNdo ? 'default' : 'secondary'}>
                    {allFormData.step3.hasNdo ? 'Yes' : 'No'}
                  </Badge>
                </div>
                {allFormData.step3.listOfNumbersToExtend && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Extension Numbers</span>
                    <span className={styles.infoValue}>{allFormData.step3.listOfNumbersToExtend}</span>
                  </div>
                )}
              </div>

              <Separator />
            </>
          )}

          {/* Step 4: Submitting Agent */}
          <div className={styles.infoList}>
            <h4 className={styles.sectionTitle}>Submitting Agent</h4>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Agency</span>
              <span className={styles.infoValue}>{allFormData.step4.agencyName || '—'}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Agent</span>
              <span className={styles.infoValue}>{agentName || '—'}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Agent Email</span>
              <span className={styles.infoValue}>{allFormData.step4.agentEmail || '—'}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Agent Phone</span>
              <span className={styles.infoValue}>{getDisplayPhone(allFormData.step4.agentPhoneNumber) || '—'}</span>
            </div>
            {allFormData.step4.hasReceivingAgent && allFormData.step4.receivingAgentEmail && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Additional Recipient</span>
                <span className={styles.infoValue}>{allFormData.step4.receivingAgentEmail}</span>
              </div>
            )}
          </div>

          <Separator />

          {/* Step 5: Services & Identifiers */}
          <div className={styles.infoList}>
            <h4 className={styles.sectionTitle}>
              <Apps />
              Services & Identifiers
            </h4>

            {(allFormData.step5.requestStartDate || allFormData.step5.requestEndDate) && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Date Range</span>
                <span className={styles.infoValue}>
                  {formatDateRange(allFormData.step5.requestStartDate, allFormData.step5.requestEndDate)}
                </span>
              </div>
            )}

            <div>
              <span className={styles.smallText}>Microsoft Services</span>
              {submittedServices.length > 0 ? (
                <div className={styles.pillGroup}>
                  {submittedServices.map((service) => (
                    <Badge key={service} variant="outline">
                      {service}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className={styles.smallText}>No Microsoft services selected.</p>
              )}
            </div>

            <div>
              <span className={styles.smallText}>Data Categories</span>
              {allFormData.step5.dataCategories.length > 0 ? (
                <div className={styles.pillGroup}>
                  {allFormData.step5.dataCategories.map((category) => (
                    <Badge key={category} variant="outline">
                      {DATA_CATEGORY_LABELS[category] || category.replace(/([A-Z])/g, ' $1').trim()}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className={styles.smallText}>No data categories selected.</p>
              )}
            </div>

            {allFormData.step5.otherDataCategories && allFormData.step5.otherDataCategories.length > 0 && (
              <div>
                <span className={styles.smallText}>Other Data Categories</span>
                <div className={styles.pillGroup}>
                  {allFormData.step5.otherDataCategories.map((category) => (
                    <Badge key={category} variant="outline">
                      {category}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div>
              <span className={styles.smallText}>Identifiers ({identifiers.length})</span>
              {identifiers.length > 0 ? (
                <div className={styles.identifierList}>
                  {identifiers.map((identifier, index) => {
                    const label = identifierTypeOptionMap[identifier.type]?.label ?? identifier.type;
                    return (
                      <div
                        key={`${identifier.type}-${identifier.value}-${index}`}
                        className={styles.identifierItem}
                      >
                        <span className={styles.identifierLabel}>{label}</span>
                        <span className={styles.identifierValue}>{identifier.value}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className={styles.smallText}>No identifiers supplied.</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Step 6: Uploaded Files */}
          <div className={styles.infoList}>
            <h4 className={styles.sectionTitle}>
              <FileText />
              Uploaded Files ({uploadedFiles.length})
            </h4>
            <div id="uploaded-files-list" style={{ display: 'flex', flexDirection: 'column', rowGap: 6 }}>
              {uploadedFiles.slice(0, showAllDetails ? uploadedFiles.length : 3).map((file) => (
                <div key={file.id} className={styles.fileRow}>
                  <span className="truncate">{file.name}</span>
                  <span className={styles.smallText}>{formatFileSize(file.size)}</span>
                </div>
              ))}
              {uploadedFiles.length > 3 && !showAllDetails && (
                <Button variant="ghost" size="sm" onClick={() => setShowAllDetails(true)} aria-expanded={showAllDetails} aria-controls="uploaded-files-list">
                  Show {uploadedFiles.length - 3} more files...
                </Button>
              )}
              <div className={styles.smallText}>
                Total size: {formatFileSize(uploadedFiles.reduce((total, file) => total + file.size, 0))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Washington Shield Law */}
      {isUSA && (
        <Card>
          <CardHeader>
            <CardTitle id={waShieldTitleId} className={styles.sectionTitle}>
              Washington Shield Law<RequiredIndicator />
            </CardTitle>
            <CardDescription id={waShieldDescId}>
              Please answer the following question about your request
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className={styles.alertSpacer}>
              <AlertDescription>
                Does your request seek documents, information, or testimony relating to an investigation into,
                or the enforcement of, a law that asserts criminal or civil liability for the provision, receipt,
                attempted provision or receipt, assistance in provision or receipt, or attempted assistance in
                the provision or receipt of gender-affirming treatment or reproductive health care services,
                that are lawful in the state of Washington?
              </AlertDescription>
            </Alert>

            <RadioGroup
              aria-labelledby={waShieldTitleId}
              aria-describedby={waShieldDescId}
              value={allFormData.step1.isWAShieldLaw === null ? '' : allFormData.step1.isWAShieldLaw.toString()}
              onValueChange={(value) => {
                const isWAShieldLaw = value === 'true';
                if (onTypeOfRequestChange) {
                  if (isWAShieldLaw) {
                    onTypeOfRequestChange({ isWAShieldLaw });
                  } else {
                    onTypeOfRequestChange({ isWAShieldLaw, waShieldLawAcknowledgement: '' });
                  }
                }
              }}
              items={[
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
              ]}
            />
            {errors.isWAShieldLaw && (
              <p className={styles.errorText}>{errors.isWAShieldLaw}</p>
            )}

            {allFormData.step1.isWAShieldLaw === true && (
              <div className={styles.ackGroup}>
                <Label htmlFor="waShieldLawAck">
                  Type &quot;I acknowledge&quot; to confirm your understanding of the Washington Shield Law implications<RequiredIndicator />
                </Label>
                <Input
                  id="waShieldLawAck"
                  value={allFormData.step1.waShieldLawAcknowledgement || ''}
                  onChange={(e) => onTypeOfRequestChange?.({ waShieldLawAcknowledgement: e.target.value })}
                  placeholder="Type: I acknowledge"
                  maxLength={15}
                />
                {errors.waShieldLawAcknowledgement && (
                  <p className={styles.errorText}>{errors.waShieldLawAcknowledgement}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!isPreservation && !isChildExploitation && (
        <Card>
          <CardHeader>
            <CardTitle className={styles.sectionTitle}>
              <Info />
              Microsoft&apos;s User Notification Policy &amp; NDO Acknowledgement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert className={styles.alertSpacer}>
              <Info />
              <AlertDescription>
                <p>{LEGAL_STRINGS.USER_NOTIFICATION_POLICY}</p>
                <p style={{ marginTop: '1rem' }}>
                  {allFormData.step3.hasNdo ? LEGAL_STRINGS.NDO_ACK_WITH_NDO : LEGAL_STRINGS.NDO_ACK_WITHOUT_NDO}
                </p>
              </AlertDescription>
            </Alert>
            <div className={styles.ackGroup}>
              <Label htmlFor="acknowledgment">
                {LEGAL_STRINGS.NDO_ACK_LABEL}<RequiredIndicator />
              </Label>
              <Input
                id="acknowledgment"
                value={acknowledgement}
                onChange={(e) => onFormChange({ acknowledgement: e.target.value })}
                placeholder="Type: I acknowledge"
                maxLength={FIELD_LIMITS.ACKNOWLEDGEMENT}
              />
              {errors.acknowledgement && (
                <p className={styles.errorText}>{errors.acknowledgement}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legal Declaration */}
      <Card>
        <CardHeader>
          <CardTitle className={styles.sectionTitle}>
            <AlertTriangle />
            Legal Declaration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert className={styles.alertSpacer}>
            <AlertTriangle />
            <AlertDescription>
              {LEGAL_STRINGS.PERJURY_DECLARATION}
            </AlertDescription>
          </Alert>
          <div className={styles.ackGroup}>
            <Label htmlFor="perjuryAcknowledgement">
              {LEGAL_STRINGS.PERJURY_ACK_LABEL}<RequiredIndicator />
            </Label>
            <Input
              id="perjuryAcknowledgement"
              value={perjuryAcknowledgement}
              onChange={(e) => onFormChange({ perjuryAcknowledgement: e.target.value })}
              placeholder="Type: I acknowledge"
              maxLength={FIELD_LIMITS.ACKNOWLEDGEMENT}
            />
            {errors.perjuryAcknowledgement && (
              <p className={styles.errorText}>{errors.perjuryAcknowledgement}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Validation Error Summary */}
      {Object.keys(errors).length > 0 && (
        <Alert>
          <AlertTriangle />
          <AlertDescription>
            <strong>Please correct the following errors before submitting:</strong>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              {Object.entries(errors).map(([key, message]) => (
                <li key={key}>{message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Submit Button */}
      <div className={styles.centerRow}>
        {getDisabledReason() && (
          <Alert className={styles.disabledButtonAlert} variant="info">
            <Info />
            <AlertDescription>{getDisabledReason()}</AlertDescription>
          </Alert>
        )}
        <Button onClick={() => setConfirmOpen(true)} disabled={isSubmitting || getDisabledReason() !== null} size="lg" className={styles.submitButton}>
          <span className={styles.submitButtonContent}>
            {isSubmitting && <Spinner size="tiny" />}
            {isSubmitting ? "Submitting..." : "Submit Data Request"}
          </span>
        </Button>
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={(_, data) => !isSubmitting && setConfirmOpen(!!data.open)}
        data-testid="confirm-submit-dialog"
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Submit request</DialogTitle>
            <DialogContent>
              Are you sure you want to create the request? You will not be able to edit the request once it is submitted.
            </DialogContent>
            <DialogActions>
              <Button
                variant="secondary"
                onClick={() => setConfirmOpen(false)}
                disabled={isSubmitting}
                data-testid="cancel-submit-button"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!isSubmitting) {
                    onSubmit();
                  }
                }}
                disabled={isSubmitting}
                data-testid="confirm-submit-button"
              >
                <span className={styles.submitButtonContent}>
                  {isSubmitting && <Spinner size="tiny" />}
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </span>
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
